import React, { forwardRef } from "react";
import {
  TypographyBase,
  TypographyProps,
  TypographyRender,
} from "../Typography";
import { Icon } from "../Icon";
import { cn, getFocusRingStyles } from "../../styles";
import { ThemeColor } from "../../props";
import { mdiOpenInNew } from "@mdi/js";

export interface LinkProps
  extends
    TypographyProps,
    Omit<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      "color" | "prefix" | "content"
    > {
  /**
   * The navigation target for the link.
   */
  to?: string;

  /**
   * If true, renders an external link icon as postfix.
   * @default false
   */
  external?: boolean;
}

/**
 * A Typography-based link component that renders as an anchor element.
 * Combines Typography styling and behavior with link functionality.
 * Supports all Typography props including render functions for router integration.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ className, to, render, external, postfix, disabled, ...props }, ref) => {
    const defaultRender: TypographyRender = ({
      className: renderedClassName,
      children,
      ...renderProps
    }) => (
      <a ref={ref} href={to} className={renderedClassName} {...renderProps}>
        {children}
      </a>
    );

    const externalIcon = external ? (
      <Icon path={mdiOpenInNew} className="w-3 h-3 ml-1" />
    ) : null;

    const finalPostfix = external ? externalIcon : postfix;

    return (
      <TypographyBase
        className={cn(
          "text-sm underline cursor-pointer",
          "outline-none focus-visible:ring-2 rounded",
          getFocusRingStyles(ThemeColor.primary),
          disabled && "pointer-events-none opacity-50 cursor-not-allowed",
          className,
        )}
        color={disabled ? ThemeColor.gray : ThemeColor.primary}
        render={render || defaultRender}
        postfix={finalPostfix}
        {...props}
      />
    );
  },
);

Link.displayName = "Link";
